import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import Logger from "../../../services/Logger";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Tab, Tabs, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import { getAllPuzzleTypes } from "../../pzl_common/PuzzleTypeConstants";
import { getColumns } from "./getColumns";
import PubTabLogs from "./PubTabLogs";
import PublisherScheduleService from "./PublisherScheduleService";

const PUZZLE_TYPE_FILTER_OPTIONS = [
    { value: "all", label: "All" },
    ...getAllPuzzleTypes().map((pt) => ({ value: pt.id, label: pt.name })),
];

const PUZZLE_TYPE_FILTER_KEY = "admin.publisher.puzzleTypeFilter";

function readStoredPuzzleTypeFilter() {
    try {
        const stored = localStorage.getItem(PUZZLE_TYPE_FILTER_KEY);
        if (!stored) return "all";
        const known = PUZZLE_TYPE_FILTER_OPTIONS.some((opt) => opt.value === stored);
        return known ? stored : "all";
    } catch {
        return "all";
    }
}

function writeStoredPuzzleTypeFilter(value) {
    try {
        localStorage.setItem(PUZZLE_TYPE_FILTER_KEY, value);
    } catch {
        /* ignore quota / private mode */
    }
}

export default function PublisherScheduleMain() {
    const navigate = useNavigate();
    const location = useLocation();
    const [schedules, setSchedules] = useState([]);
    const [scheduleOptions, setScheduleOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reload, setReload] = useState();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const [totalRows, setTotalRows] = useState(0);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState("info");
    const [message, setMessage] = useState("");

    const [tabValue, setTabValue] = useState(0);
    const [puzzleTypeFilter, setPuzzleTypeFilter] = useState(readStoredPuzzleTypeFilter);
    const [duplicatingId, setDuplicatingId] = useState(null);
    const [removingId, setRemovingId] = useState(null);
    const [rowToDelete, setRowToDelete] = useState(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    useEffect(() => {
        fetchSchedules();
    }, [reload, paginationModel, puzzleTypeFilter]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const result = await PublisherScheduleService.listSchedules(0, 200, "createdAt:desc");
            if (!cancelled && result.success && result.data) {
                setScheduleOptions(result.data.content || []);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [reload]);

    const fetchSchedules = async () => {
        setLoading(true);
        const result = await PublisherScheduleService.listSchedules(
            paginationModel.page,
            paginationModel.pageSize,
            "createdAt:desc",
            puzzleTypeFilter
        );
        if (result.success && result.data) {
            setSchedules(result.data.content || []);
            setTotalRows(result.data.page?.totalElements ?? 0);
        } else {
            handleSnackBar("error", result.error || "Failed to fetch schedules");
        }
        setLoading(false);
    };

    const handleSnackBar = (severity, message) => {
        setOpenSnackbar(true);
        setSeverity(severity);
        setMessage(message);
    };

    const handleOpenCreate = () => {
        navigate("/admin/publisher/new");
    };

    useEffect(() => {
        const msg = location.state?.message;
        if (msg) {
            setSeverity("success");
            setMessage(msg);
            setOpenSnackbar(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.message, location.pathname, navigate]);

    const handleToggleEnabled = async (schedule, newEnabledValue) => {
        try {
            const result = await PublisherScheduleService.updateSchedule(schedule.id, { enabled: newEnabledValue });
            if (result.success) {
                setSchedules((prev) =>
                    prev.map((item) =>
                        item.id === schedule.id ? { ...item, enabled: newEnabledValue } : item
                    )
                );
                handleSnackBar("success", `Schedule ${newEnabledValue ? "enabled" : "disabled"} successfully`);
            } else {
                handleSnackBar("error", result.error || "Failed to update enabled status");
                setReload(moment());
            }
        } catch (error) {
            Logger.error("Error toggling enabled status:", error);
            handleSnackBar("error", "Failed to update enabled status. Please try again.");
            setReload(moment());
        }
    };

    const handleDuplicate = async (row) => {
        if (!row?.id || duplicatingId) return;
        setDuplicatingId(row.id);
        const loaded = await PublisherScheduleService.getSchedule(row.id);
        const source = loaded.success && loaded.data ? loaded.data : row;
        const memoText = typeof source.memo === "string" ? source.memo.trim() : "";
        const result = await PublisherScheduleService.createSchedule({
            cron: source.cron,
            language: source.language,
            puzzleType: source.puzzleType,
            enabled: false,
            tagBasedCrossword: Boolean(source.tagBasedCrossword),
            memo: memoText ? `${memoText} (copy)` : "Copy",
        });
        setDuplicatingId(null);
        if (result.success) {
            handleSnackBar("success", "Schedule copied (disabled until you enable it)");
            setReload(moment());
        } else {
            handleSnackBar("error", result.error || "Failed to copy schedule");
        }
    };

    const handleRemoveClick = (row) => {
        if (!row?.id || duplicatingId || removingId) return;
        setRowToDelete(row);
        setOpenConfirmDialog(true);
    };

    const handleConfirmDelete = async (id) => {
        if (!id) return;
        setRemovingId(id);
        const result = await PublisherScheduleService.deleteSchedule(id);
        setRemovingId(null);
        setRowToDelete(null);
        if (result.success) {
            handleSnackBar("success", "Schedule removed");
            setReload(moment());
        } else {
            handleSnackBar("error", result.error || "Failed to remove schedule");
        }
    };

    const columns = getColumns({
        onToggleEnabled: handleToggleEnabled,
        onDuplicate: handleDuplicate,
        onRemove: handleRemoveClick,
        duplicatingId,
        removingId,
    });

    const handleRowClick = (params) => {
        navigate(`/admin/publisher/edit/${params.id}`);
    };

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h5" gutterBottom>
                    Publisher Schedule
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                >
                    Add Schedule
                </Button>
            </Box>

            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tab label="Schedules" />
                <Tab label="Logs" />
            </Tabs>

            {tabValue === 0 && (
                <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel id="publisher-schedule-puzzle-type-label">Puzzle Type</InputLabel>
                            <Select
                                labelId="publisher-schedule-puzzle-type-label"
                                id="publisher-schedule-puzzle-type"
                                value={puzzleTypeFilter}
                                label="Puzzle Type"
                                onChange={(e) => {
                                    setPuzzleTypeFilter(e.target.value);
                                    writeStoredPuzzleTypeFilter(e.target.value);
                                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                                }}
                            >
                                {PUZZLE_TYPE_FILTER_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <DataGrid
                    rows={schedules}
                    columns={columns}
                    getRowId={(row) => row.id}
                    loading={loading}
                    paginationMode="server"
                    rowCount={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                    onRowClick={handleRowClick}
                    autoHeight
                    sx={{ minHeight: 400, "& .MuiDataGrid-row": { cursor: "pointer" } }}
                />
                </>
            )}

            {tabValue === 1 && (
                <PubTabLogs schedules={scheduleOptions} onError={handleSnackBar} />
            )}

            <ConfirmDialog
                open={openConfirmDialog}
                setOpen={setOpenConfirmDialog}
                title="Remove schedule?"
                message="This removes the publisher schedule and reloads Quartz on the server. This cannot be undone."
                targetName={rowToDelete?.memo || rowToDelete?.cron || ""}
                targetId={rowToDelete?.id}
                callback={handleConfirmDelete}
                okText="Remove"
                cancelText="Cancel"
            />

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />
        </Box>
    );
}
